import { useParams, useNavigate } from 'react-router-dom';
import { useClinicBasePath } from '@/hooks/useClinicBasePath';
import { useQuery } from '@tanstack/react-query';
import {
  Page,
  Card,
  BlockStack,
  InlineStack,
  Text,
  Badge,
  DataTable,
  SkeletonBodyText,
  EmptyState,
  Box,
} from '@shopify/polaris';
import { clinicPortalApi } from '@/services';
import type { Patient, Rental } from '@/types';
import { orderNumber } from '@/utils/orderNumber';

function getStatusBadge(status: string) {
  const toneMap: Record<string, 'success' | 'attention' | 'critical' | 'info' | undefined> = {
    in_use: 'success',
    shipped: 'info',
    processing: 'attention',
    overdue: 'critical',
    extended: 'attention',
    complete: undefined,
    pending: 'attention',
  };
  return <Badge tone={toneMap[status]}>{status.replace(/_/g, ' ')}</Badge>;
}

export default function ClinicPatientDashboard() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const base = useClinicBasePath();

  // Fetch all patients to find the specific one
  const { data: patientsResponse, isLoading: patientsLoading } = useQuery({
    queryKey: ['clinic-patients', 1, ''],
    queryFn: () => clinicPortalApi.getPatients({ page: 1, limit: 500 }),
  });

  const patient: Patient | undefined = patientsResponse?.data?.find(
    (p: Patient) => p.id === id,
  );

  // Fetch orders and filter for this patient
  const { data: ordersResponse, isLoading: ordersLoading } = useQuery({
    queryKey: ['clinic-orders-patient', id],
    queryFn: () => clinicPortalApi.getOrders({ page: 1, limit: 100, search: patient?.firstName }),
    enabled: !!patient,
  });

  const patientOrders = (ordersResponse?.data || []).filter(
    (r: Rental) => r.patientId === id,
  );

  const isLoading = patientsLoading || ordersLoading;

  if (isLoading) {
    return (
      <Page title="Patient Dashboard" backAction={{ content: 'Patients', onAction: () => navigate(`${base}/patients`) }}>
        <Card><SkeletonBodyText lines={8} /></Card>
      </Page>
    );
  }

  if (!patient) {
    return (
      <Page title="Patient Not Found" backAction={{ content: 'Patients', onAction: () => navigate(`${base}/patients`) }}>
        <Card>
          <EmptyState
            heading="Patient not found"
            image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
          >
            <p>This patient could not be found.</p>
          </EmptyState>
        </Card>
      </Page>
    );
  }

  const orderRows = patientOrders.map((rental: Rental) => [
    orderNumber(rental),
    rental.items.map((i) => i.productName).join(', ') || 'N/A',
    rental.rentalStartDate ? new Date(rental.rentalStartDate).toLocaleDateString() : 'N/A',
    rental.rentalEndDate ? new Date(rental.rentalEndDate).toLocaleDateString() : 'N/A',
    `$${Number(rental.totalAmount).toFixed(2)}`,
    getStatusBadge(rental.rentalStatus),
  ]);

  return (
    <Page
      title={`${patient.firstName} ${patient.lastName}`}
      subtitle="Patient Dashboard"
      backAction={{ content: 'Patients', onAction: () => navigate(`${base}/patients`) }}
      primaryAction={{
        content: 'Create Order',
        onAction: () => navigate(`${base}/orders/new?patientId=${patient.id}`),
      }}
    >
      <BlockStack gap="400">
        {/* Patient Info */}
        <Card>
          <BlockStack gap="200">
            <Text as="h3" variant="headingMd">Patient Information</Text>
            <InlineStack gap="800">
              <BlockStack gap="100">
                <Text as="p" tone="subdued">Email</Text>
                <Text as="p">{patient.email || 'N/A'}</Text>
              </BlockStack>
              <BlockStack gap="100">
                <Text as="p" tone="subdued">Phone</Text>
                <Text as="p">{patient.phone || 'N/A'}</Text>
              </BlockStack>
              <BlockStack gap="100">
                <Text as="p" tone="subdued">Gender</Text>
                <Text as="p">{patient.gender || 'N/A'}</Text>
              </BlockStack>
              <BlockStack gap="100">
                <Text as="p" tone="subdued">Address</Text>
                <Text as="p">
                  {[patient.streetAddress, patient.city, patient.province, patient.postalCode]
                    .filter(Boolean)
                    .join(', ') || 'N/A'}
                </Text>
              </BlockStack>
            </InlineStack>
            {patient.referredByDoctor && (
              <Box paddingBlockStart="200">
                <Text as="p" tone="subdued">
                  Referred by: Dr. {patient.referredByDoctor.firstName} {patient.referredByDoctor.lastName}
                </Text>
              </Box>
            )}
          </BlockStack>
        </Card>

        {/* Order History */}
        <Card>
          <BlockStack gap="200">
            <Text as="h3" variant="headingMd">Order History</Text>
            {orderRows.length === 0 ? (
              <Box padding="400">
                <Text as="p" tone="subdued" alignment="center">
                  No orders found for this patient.
                </Text>
              </Box>
            ) : (
              <DataTable
                columnContentTypes={['text', 'text', 'text', 'text', 'numeric', 'text']}
                headings={['Order #', 'Product', 'Start Date', 'End Date', 'Total', 'Status']}
                rows={orderRows}
              />
            )}
          </BlockStack>
        </Card>
      </BlockStack>
    </Page>
  );
}
